import { Grid2 } from '@mui/material'
import { useContext, useMemo } from 'react'
import { CustomSelect, selectTypes } from 'src/consumer/components/helperComponents'
import { UnsecureSetupContext } from 'src/consumer/context/UnsecureSetupContext'
// Hooks
import { usePatientForm } from './hooks/usePatientForm'
import { usePatientValidation } from './hooks/usePatientValidation'
import { usePatientSearch } from './hooks/usePatientSearch'
import { useFormFieldVisibility } from './hooks/useFormFieldVisibility'
// Components
import PatientBasicInfoFields from './components/PatientBasicInfoFields'
import PatientMoreDetails from './components/PatientMoreDetails'
import { FormField } from './components/FormField'
import {
  buildPatientFormI18n,
  PatientFormI18nContext,
} from './utils/patientFormI18n'

export default function PatientFormComponentV2({
  editMode = false,
  patientDetails,
  setPatientDetails,
  hidePatientNumber = false,
  hideInputValidations = false,
  makeAnonymousPatient,
  onFindingExistingPatient,
  addRelatedPatient,
  hideRelationShipDetails = false,
  clinicId,
  clinicBrandId,
  setMakeAnonymousPatient,
  importIdFieldLabel = 'Import Id',
  isAllowedToAddImportId = false,
  isAllowedToAddUHID = false,
  isForcingAnonymousPatient = false,
  moveEmailToMoreSettings = false,
  disablePatientSearch = false,
  showAdminFields = false,
  dontUseMoreDetailsAccordian,
  autoFocusMobileField = false,
  patientSearchApiSignature = '/api/secure/mentor/patient/list',
  formUiLanguageCode = 'en',
  onChangeFormUiLanguage,
  showFormUiLanguageSelector = false,
}) {
  const { unSecureSetupData } = useContext(UnsecureSetupContext)
  const patientFormI18n = useMemo(
    () => buildPatientFormI18n(formUiLanguageCode, unSecureSetupData?.appLanguages),
    [formUiLanguageCode, unSecureSetupData?.appLanguages],
  )

  // Initialize hooks
  const formHook = usePatientForm(
    patientDetails,
    setPatientDetails,
    makeAnonymousPatient,
    setMakeAnonymousPatient,
  )

  const validationHook = usePatientValidation(
    patientDetails,
    makeAnonymousPatient,
    hideInputValidations,
    patientFormI18n.translateValidationMessage,
  )

  const searchHook = usePatientSearch(
    patientDetails,
    clinicId,
    clinicBrandId,
    addRelatedPatient,
    onFindingExistingPatient,
    setMakeAnonymousPatient,
    patientSearchApiSignature,
  )

  const visibilityHook = useFormFieldVisibility({
    patientDetails,
    makeAnonymousPatient,
    isForcingAnonymousPatient,
    moveEmailToMoreSettings,
    hidePatientNumber,
    isAllowedToAddUHID,
    isAllowedToAddImportId,
    hideRelationShipDetails,
    initialVisibilityConfig: {
      communicationToPref: showAdminFields,
    },
  })

  return (
    <PatientFormI18nContext.Provider value={patientFormI18n}>
      <div className="d-flex flex-column gap-2">
        {showFormUiLanguageSelector && (
          <Grid2 container spacing={2}>
            <FormField
              label={patientFormI18n.copy.formLanguageLabel}
              helperText={patientFormI18n.copy.formLanguageHelper}
              gridConfig={{ xs: 12 }}
            >
              <CustomSelect
                isDisabled={!editMode || !onChangeFormUiLanguage}
                value={patientFormI18n.uiLanguageCode}
                id="formUiLanguage"
                options={patientFormI18n.uiLanguageOptions}
                getOptionValue={(option) => option?.id}
                getOptionLabel={(option) =>
                  option?.id === 'en'
                    ? option?.language
                    : `${option?.language} (${option?.localLanguageName})`
                }
                menuPlacement="auto"
                selectType={selectTypes.string}
                onChangeEvent={(event) => onChangeFormUiLanguage?.(event.target.value)}
              />
            </FormField>
          </Grid2>
        )}

        <PatientBasicInfoFields
          patientDetails={patientDetails}
          editMode={editMode}
          formHook={formHook}
          validationHook={validationHook}
          searchHook={searchHook}
          visibilityHook={visibilityHook}
          makeAnonymousPatient={makeAnonymousPatient}
          isForcingAnonymousPatient={isForcingAnonymousPatient}
          importIdFieldLabel={importIdFieldLabel}
          clinicId={clinicId}
          clinicBrandId={clinicBrandId}
          patientSearchApiSignature={patientSearchApiSignature}
          disablePatientSearch={disablePatientSearch}
          autoFocusMobileField={autoFocusMobileField}
        />

        <PatientMoreDetails
          dontUseMoreDetailsAccordian={dontUseMoreDetailsAccordian}
          patientDetails={patientDetails}
          editMode={editMode}
          formHook={formHook}
          validationHook={validationHook}
          visibilityHook={visibilityHook}
          makeAnonymousPatient={makeAnonymousPatient}
        />
      </div>
    </PatientFormI18nContext.Provider>
  )
}
